
import etiquetasService from "@/modules/Etiquetas/services/etiquetasService";
import { scopeLabel } from "@/modules/Etiquetas/services/etiquetaDefinition";

const emptyForm = () => ({
  name: "",
  description: "",
  preset: "termica_50x30",
  scope: "global",
  companyId: null,
});

export default {
  name: "EtiquetasIndex",
  data() {
    return {
      loading: true,
      saving: false,
      submitted: false,
      search: "",
      tab: "todos",
      catalog: { legados: [], novos: [], totais: {} },
      presets: [],
      modalMode: "new",
      selectedLegacy: null,
      form: emptyForm(),
      companySearch: "",
      companies: [],
      selectedCompany: null,
      searchingCompanies: false,
      companyTimer: null,
      discardingUuid: null,
    };
  },
  computed: {
    normalizedSearch() {
      return this.normalize(this.search);
    },
    filteredNew() {
      return (this.catalog.novos || []).filter((model) => {
        const match =
          !this.normalizedSearch ||
          this.normalize(
            `${model.nome} ${model.descricao} ${model.codigo_seletor}`
          ).includes(this.normalizedSearch);
        const tabMatch = this.tab !== "rascunhos" || model.tem_rascunho;
        return match && tabMatch;
      });
    },
    filteredLegacy() {
      return (this.catalog.legados || []).filter((row) => {
        const match =
          !this.normalizedSearch ||
          this.normalize(`${row.nome} ${row.codigo_legado}`).includes(
            this.normalizedSearch
          );
        const hasDraft =
          row.estado === "rascunho" ||
          Boolean(row.modelo && row.modelo.tem_rascunho) ||
          (row.variantes || []).some((variant) => variant.tem_rascunho);
        const tabMatch = this.tab !== "rascunhos" || hasDraft;
        return match && tabMatch;
      });
    },
    showNewSection() {
      return ["todos", "editor", "rascunhos"].includes(this.tab);
    },
    showLegacySection() {
      return ["todos", "legados", "rascunhos"].includes(this.tab);
    },
    selectedLegacyName() {
      return this.selectedLegacy
        ? `${this.selectedLegacy.nome} · ${this.selectedLegacy.codigo_legado}`
        : "Modelo legado";
    },
  },
  async mounted() {
    await this.load();
    if (this.$route.query.novo === "1") {
      this.openCreateModal();
      this.$router.replace({ name: "etiquetasLista" }).catch(() => {});
    }
  },
  beforeDestroy() {
    if (this.companyTimer) clearTimeout(this.companyTimer);
  },
  methods: {
    scopeLabel,
    async load() {
      this.loading = true;
      try {
        const [bootstrap, models] = await Promise.all([
          etiquetasService.inicializacao(),
          etiquetasService.modelos(),
        ]);
        this.presets = bootstrap.data.presets || [];
        this.catalog = models.data || { legados: [], novos: [], totais: {} };
      } catch (error) {
        this.catalog = { legados: [], novos: [], totais: {} };
      } finally {
        this.loading = false;
      }
    },
    normalize(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    stateLabel(state) {
      return (
        {
          legado_protegido: "Legado protegido",
          rascunho: "Rascunho do editor",
          publicado_editor: "Editor em uso",
        }[state] || state
      );
    },
    stateClass(state) {
      return state === "publicado_editor"
        ? "published"
        : state === "rascunho"
        ? "draft"
        : "legacy";
    },
    versionLabel(model) {
      if (model.tem_rascunho) return `Rascunho v${model.versao_rascunho}`;
      if (model.tem_publicacao) return `Versão ${model.versao_publicada}`;
      return "Ainda não publicado";
    },
    editModel(model) {
      this.$router.push({
        name: "etiquetasEditar",
        params: { uuid: model.uuid },
      });
    },
    openCreateModal() {
      this.resetModal();
      this.modalMode = "new";
      this.$bvModal.show("etiqueta-create-modal");
    },
    openLegacyModal(row) {
      this.resetModal();
      this.modalMode = "legacy";
      this.selectedLegacy = row;
      this.$bvModal.show("etiqueta-create-modal");
    },
    resetModal() {
      this.submitted = false;
      this.saving = false;
      this.form = emptyForm();
      this.selectedLegacy = null;
      this.companySearch = "";
      this.companies = [];
      this.selectedCompany = null;
    },
    selectScope(scope) {
      this.form.scope = scope;
      if (scope === "global") this.clearCompany();
      else this.searchCompanies();
    },
    scheduleCompanySearch() {
      if (this.companyTimer) clearTimeout(this.companyTimer);
      this.companyTimer = setTimeout(this.searchCompanies, 280);
    },
    async searchCompanies() {
      this.searchingCompanies = true;
      try {
        const { data } = await etiquetasService.buscarEmpresas(
          this.companySearch
        );
        this.companies = Array.isArray(data) ? data : [];
      } catch (error) {
        this.companies = [];
      } finally {
        this.searchingCompanies = false;
      }
    },
    chooseCompany(company) {
      this.selectedCompany = company;
      this.form.companyId = company.id_empresa;
      this.companies = [];
    },
    clearCompany() {
      this.selectedCompany = null;
      this.form.companyId = null;
      this.companySearch = "";
      this.companies = [];
    },
    companyName(company) {
      return (
        company.nome_fantasia || company.nome || `Empresa ${company.id_empresa}`
      );
    },
    async submitModal() {
      this.submitted = true;
      const invalidName = this.modalMode === "new" && !this.form.name;
      const invalidCompany =
        this.form.scope === "empresa" && !this.form.companyId;
      if (invalidName || invalidCompany) return;

      this.saving = true;
      try {
        const scope = {
          escopo: this.form.scope,
          id_empresa_destino:
            this.form.scope === "empresa" ? this.form.companyId : null,
        };
        const response =
          this.modalMode === "legacy"
            ? await etiquetasService.iniciarLegado({
                codigo_legado: this.selectedLegacy.codigo_legado,
                ...scope,
              })
            : await etiquetasService.criar({
                nome: this.form.name,
                descricao: this.form.description || null,
                preset: this.form.preset,
                ...scope,
              });
        this.$bvModal.hide("etiqueta-create-modal");
        this.$toast.success(
          this.modalMode === "legacy"
            ? "Rascunho criado. A etiqueta original continua ativa."
            : "Modelo criado como rascunho."
        );
        this.editModel(response.data);
      } catch (error) {
        this.saving = false;
      }
    },
    async duplicateModel(model) {
      const result = await this.$swal.fire({
        title: "Duplicar modelo",
        text: "A cópia será criada como rascunho e não afetará a impressão.",
        input: "text",
        inputValue: `${model.nome} - Cópia`,
        inputLabel: "Nome da cópia",
        showCancelButton: true,
        confirmButtonText: "Criar cópia",
        cancelButtonText: "Cancelar",
        inputValidator: (value) =>
          !String(value || "").trim() ? "Informe um nome." : undefined,
      });
      if (!result.isConfirmed) return;
      try {
        const { data } = await etiquetasService.duplicar({
          modelo_uuid: model.uuid,
          nome: result.value,
        });
        this.$toast.success("Cópia criada como rascunho.");
        this.editModel(data);
      } catch (error) {
        // O interceptor apresenta a mensagem da API.
      }
    },
    async archiveModel(model) {
      const result = await this.$swal.fire({
        icon: "warning",
        title: "Arquivar este modelo?",
        html: `<p>O modelo <strong>${this.escapeHtml(
          model.nome
        )}</strong> deixará de aparecer para impressão.</p><p class="text-muted font-size-sm">Esta ação não apaga as etiquetas legadas.</p>`,
        showCancelButton: true,
        confirmButtonText: "Sim, arquivar",
        confirmButtonColor: "#f64e60",
        cancelButtonText: "Cancelar",
      });
      if (!result.isConfirmed) return;
      try {
        await etiquetasService.arquivar(model.uuid, model.token_revisao);
        this.$toast.success("Modelo arquivado.");
        await this.load();
      } catch (error) {
        // O interceptor apresenta a mensagem da API.
      }
    },
    discardDraftLabel(model) {
      return model.origem === "legado" && !model.tem_publicacao
        ? "Cancelar alterações e usar legado"
        : "Excluir rascunho";
    },
    async discardDraft(model) {
      if (this.discardingUuid) return;
      const returnsToLegacy =
        model.origem === "legado" && !model.tem_publicacao;
      const removesModel = model.origem !== "legado" && !model.tem_publicacao;
      const result = await this.$swal.fire({
        icon: "warning",
        title: returnsToLegacy
          ? "Cancelar alterações e usar o legado?"
          : "Excluir este rascunho?",
        text: returnsToLegacy
          ? "A versão editável será removida e a etiqueta legada continuará em uso."
          : removesModel
          ? "Este modelo ainda não foi publicado e será removido da lista."
          : "As alterações em andamento serão removidas. A versão publicada continuará em uso.",
        showCancelButton: true,
        confirmButtonText: this.discardDraftLabel(model),
        confirmButtonColor: "#f64e60",
        cancelButtonText: "Voltar",
      });
      if (!result.isConfirmed) return;

      this.discardingUuid = model.uuid;
      try {
        await etiquetasService.descartarRascunho(
          model.uuid,
          model.token_revisao
        );
        this.$toast.success(
          returnsToLegacy
            ? "Alterações canceladas. A etiqueta legada segue em uso."
            : "Rascunho excluído."
        );
        await this.load();
      } catch (error) {
        // O interceptor apresenta a mensagem da API.
      } finally {
        this.discardingUuid = null;
      }
    },
    escapeHtml(value) {
      const div = document.createElement("div");
      div.textContent = String(value || "");
      return div.innerHTML;
    },
  },
};
